import {
  Users, Clock, CalendarDays, Wallet, BarChart3, ShieldCheck,
  Smartphone, FileText, Building2, Bell, Workflow, IdCard,
  Fingerprint, MapPin, Receipt, TrendingUp, CalendarClock, UserCheck, Languages
} from "lucide-react";

export const modules = [
  {
    slug: "employee-management",
    icon: Users,
    name: "Employee Management",
    tagline: "One source of truth for your entire workforce",
    problem: "Employee data scattered across spreadsheets, emails, and legacy systems creates costly errors, compliance risk, and slow HR response times.",
    solution: "Centralize every employee record — from personal details and family relations to job history, salary, documents and access cards — in a secure, audit-ready workspace.",
    benefits: [
      "Eliminate data silos with a unified employee database",
      "Onboard 3x faster with multi-section digital forms",
      "Stay audit-ready with automatic change history",
      "Personalize offer letters with dynamic tokens",
    ],
    features: [
      "Active & separated employee lists",
      "Multi-section employee profiles",
      "Employment type configuration",
      "Job role & position history",
      "Documents with dynamic fields",
      "Warning letters & disciplinary tracking",
      "Drag-and-drop offer letter builder",
      "Custom letter template library",
      "Employment timeline visualization",
      "Policy assignment per employee",
      "Shift assignment & monthly roster",
      "Dynamic token insertion in letters",
    ],
    useCases: [
      "Manufacturing HR teams tracking 500+ shop-floor workers across shifts",
      "IT firms managing distributed hiring with region-specific policies",
      "Healthcare providers maintaining compliance-ready credential records",
    ],
  },
  {
    slug: "attendance-management",
    icon: Clock,
    name: "Attendance Management",
    tagline: "Accurate time capture for every work model",
    problem: "Manual attendance, buddy punching, and gaps between biometric devices and payroll cost businesses up to 4% of gross payroll each year.",
    solution: "Capture attendance from biometric devices, web punch with camera + GPS, and mobile beacons — then automate exception handling and approvals end-to-end.",
    benefits: [
      "Cut attendance disputes by up to 90%",
      "Support office, remote, hybrid, and field teams from one system",
      "Auto-flag late arrivals with configurable tolerance",
      "Bridge attendance to payroll with zero re-keying",
    ],
    features: [
      "Biometric device integration & management",
      "Web punch with camera and location",
      "Beacon-based mobile punch authorization",
      "Punch approval workflows",
      "Attendance types configuration",
      "Late & missed clock-in tolerance rules",
      "Attendance round-off settings",
      "Exception regularization (CI/CO pairs)",
      "Real-time 'Who is here' dashboard",
      "Attendance comparison reports",
    ],
    useCases: [
      "Retail chains tracking multi-store shift attendance in real time",
      "Construction firms verifying field crews with GPS-anchored punches",
      "BPOs enforcing shift discipline with tolerance-based auto-alerts",
    ],
  },
  {
    slug: "leave-management",
    icon: CalendarDays,
    name: "Leave Management",
    tagline: "Automated leave policies that scale with your business",
    problem: "Manual leave tracking creates balance disputes, missed accruals, and last-minute staffing crises that hit customer service and productivity.",
    solution: "Design any leave policy visually — accruals, tenure-based rules, holiday policies, weekend logic — and let the rules engine handle the rest.",
    benefits: [
      "Reduce HR queries about balances by 70%+",
      "Prevent understaffing with visual team leave calendars",
      "Automate accruals for monthly, annual, and attendance-based rules",
      "Configure region-specific holiday policies in minutes",
    ],
    features: [
      "Apply leave on behalf of employees",
      "Multi-level leave approval workflows",
      "Individual & org-wide leave balances",
      "Configurable leave accrual policies",
      "Attendance-based accrual rules",
      "Per-employee accrual overrides",
      "Holiday list & holiday policies",
      "Weekend policy configuration",
      "Holiday & leave calendars",
      "Tolerance policies per leave type",
    ],
    useCases: [
      "Schools automating academic-calendar-based leave rules",
      "Hospitality groups balancing weekend and holiday coverage",
      "Global IT teams managing region-specific leave entitlements",
    ],
  },
  {
    slug: "payroll",
    icon: Wallet,
    name: "Payroll",
    tagline: "Zero-error payroll, every single cycle",
    problem: "Payroll errors erode trust, invite legal risk, and consume days of finance and HR bandwidth every month.",
    solution: "Run payroll from a single dashboard with configurable heads, allowances, deductions, and attendance-linked calculations — with a branded payslip designer.",
    benefits: [
      "Close payroll in hours instead of days",
      "Cut payroll disputes with employee self-service payslips",
      "Stay compliant with configurable statutory heads",
      "Give finance a clean daily view of payroll costs",
    ],
    features: [
      "Payroll dashboard & processing",
      "Employee payroll self-service center",
      "Salary structure management",
      "Advance salary requests & approvals",
      "Configurable deductions & penalties",
      "Custom allowance settings",
      "Payroll head configuration",
      "Salary slip template designer",
      "PDF payslip generation & download",
      "Daily salary and payroll reports",
    ],
    useCases: [
      "Manufacturing running attendance-linked wage payouts weekly",
      "Professional services firms handling variable allowances by project",
      "Retail groups paying across multiple locations from one console",
    ],
  },
  {
    slug: "reports-analytics",
    icon: BarChart3,
    name: "Reports & Analytics",
    tagline: "Turn workforce data into board-ready decisions",
    problem: "HR leaders spend weeks compiling spreadsheets — and still cannot answer basic questions about attendance, cost, or attrition on demand.",
    solution: "Access 13 pre-built HR reports, customizable dashboards, and audit-ready timesheets that surface answers in seconds, not weeks.",
    benefits: [
      "Answer executive questions with real-time dashboards",
      "Simplify audits with tamper-evident event history",
      "Spot attendance risks before they become payroll issues",
      "Give department heads their own live analytics view",
    ],
    features: [
      "Event Sheet Report (historical audit log)",
      "Modern timesheet reports with audit trail",
      "Attendance overview & corrections",
      "Attendance comparison across sources",
      "Exception & penalty reports",
      "Punch approval report",
      "Real-time 'Who is here' report",
      "Daily clock in/out summary",
      "Daily salary & payroll reports",
      "Late coming & regularization analytics",
    ],
    useCases: [
      "CFOs monitoring daily labor cost across locations",
      "HR heads spotting attendance risk before payroll close",
      "Auditors reviewing tamper-evident event history",
    ],
  },
  {
    slug: "employee-self-service",
    icon: Smartphone,
    name: "Employee Self Service",
    tagline: "Empower employees. Free your HR team.",
    problem: "HR teams drown in repetitive queries — leave balances, payslip requests, attendance corrections — leaving no time for strategic work.",
    solution: "Give every employee a self-service portal to punch in, apply for leave, download payslips, and raise regularization requests — from any device.",
    benefits: [
      "Reduce HR ticket volume by up to 60%",
      "Improve employee experience with instant answers",
      "Cut paper-based document requests to zero",
      "Boost adoption with a mobile-first experience",
    ],
    features: [
      "Web clock in/out with camera & GPS",
      "Apply leave & view real-time balance",
      "Download monthly payslips",
      "Personal document center",
      "Holiday & leave calendars",
      "Exception regularization requests",
      "Work-from-home requests",
      "Profile & password management",
      "Personalized dashboard widgets",
      "Real-time notifications",
    ],
    useCases: [
      "Distributed IT teams managing everything from mobile",
      "Field sales teams punching in from client sites",
      "Hospital staff downloading payslips without HR queue",
    ],
  },
  {
    slug: "mobile-workforce",
    icon: Smartphone,
    name: "Mobile Workforce",
    tagline: "HR in your team's pocket — on their terms",
    problem: "Desk-less workers, field crews, and remote staff are left out of legacy HR systems built only for the office.",
    solution: "A native mobile app with beacon-based punch, remote work requests, mobile approvals, and full permission control from the web admin.",
    benefits: [
      "Give every worker a modern HR experience",
      "Approve leaves and punches on the go",
      "Secure field attendance with beacons & GPS",
      "Centrally control mobile permissions per role",
    ],
    features: [
      "Native mobile punch (camera + GPS)",
      "Beacon-based punch authorization",
      "Apply leave from mobile",
      "Leave balance & mobile approvals",
      "Work-from-home apply & tracking",
      "Mobile 'Who is here' view",
      "Manual attendance report on mobile",
      "Admin access via mobile app",
      "Full permission layer from web admin",
      "Responsive across desktop & mobile",
    ],
    useCases: [
      "Logistics companies tracking drivers across geographies",
      "Retail managers approving shifts between store visits",
      "Hospitality supervisors punching seasonal staff on site",
    ],
  },
  {
    slug: "security-access",
    icon: ShieldCheck,
    name: "Security & Access Control",
    tagline: "Enterprise-grade security, without the enterprise complexity",
    problem: "HR data is among the most sensitive in any organization — and yet often lives in shared drives with no meaningful access controls.",
    solution: "Role-based access, feature-level permissions, MFA, tenant isolation, and full audit trails — configured in minutes, not months.",
    benefits: [
      "Meet enterprise security requirements out of the box",
      "Isolate data per tenant / branch / department",
      "Track every policy and status change automatically",
      "Support dual-panel access for HR admins",
    ],
    features: [
      "Role-based permissions (View/Add/Edit/Delete)",
      "Feature-level access control",
      "User-level permission overrides",
      "MFA / OTP authentication",
      "Tenant & subdomain isolation",
      "Dual-panel access for HR staff",
      "Profile & password policies",
      "Audit trail on policy & status changes",
      "Branch-level settings",
      "Custom company branding & themes",
      "Dual-language support (English & Arabic)",
    ],
    useCases: [
      "Multi-entity holdings segregating data across companies",
      "Healthcare providers meeting patient privacy standards",
      "Enterprise HR controlling access down to feature level",
    ],
  },
  {
    slug: "shift-scheduling",
    icon: CalendarClock,
    name: "Shift Scheduling",
    tagline: "Rosters that plan themselves",
    problem: "Manual shift planning across teams, sites, and time zones is the single biggest source of last-minute HR chaos — and understaffing.",
    solution: "Assign shifts, publish monthly rosters, and let attendance rules flow automatically into payroll — for every shop, plant, hospital ward, or store.",
    benefits: [
      "Publish rosters in minutes, not days",
      "Prevent understaffing with visual coverage views",
      "Auto-link shifts to attendance and pay",
      "Handle rotating, split, and overnight shifts",
    ],
    features: [
      "Shift assignment per employee",
      "Monthly roster generation",
      "Rotating & split shift support",
      "Overnight and cross-day shifts",
      "Weekend and holiday-aware rosters",
      "Roster-based attendance validation",
      "Branch- and department-level shifts",
      "Bulk shift updates",
      "Shift-based payroll calculation",
      "Roster export and print",
    ],
    useCases: [
      "Hospitals rostering nurses across 24/7 wards",
      "Manufacturing plants planning three-shift operations",
      "Retail chains scheduling part-time staff by store",
    ],
  },
  {
    slug: "visitor-management",
    icon: UserCheck,
    name: "Visitor Management",
    tagline: "A modern front desk for a modern workplace",
    problem: "Paper visitor logs are unreadable, unsearchable, and useless the moment an auditor — or an incident — arrives.",
    solution: "Register, track, and report on every visitor entering your premises, with digital logs your security and audit teams can actually use.",
    benefits: [
      "Digitize the front desk in minutes",
      "Improve premises security and accountability",
      "Provide auditors with searchable visitor logs",
      "Track visitor traffic for facilities planning",
    ],
    features: [
      "Visitor registration & check-in",
      "Host and purpose tracking",
      "Visitor duration & timesheet",
      "Visitor timesheet reporting",
      "Digital visitor logs (audit-ready)",
      "Branch-level visitor policies",
      "Photo capture at check-in",
      "Bulk visitor exports",
      "Security team notifications",
      "Integration with access cards",
    ],
    useCases: [
      "Manufacturing plants meeting site-audit requirements",
      "Corporate offices replacing paper visitor books",
      "Hospitals tracking non-staff entering restricted zones",
    ],
  },
];

export const quickModules = [
  { icon: Users, name: "Employee Management", href: "/software/employee-management" },
  { icon: Fingerprint, name: "Attendance", href: "/software/attendance-management" },
  { icon: CalendarDays, name: "Leave", href: "/software/leave-management" },
  { icon: Wallet, name: "Payroll", href: "/software/payroll" },
  { icon: BarChart3, name: "Reports", href: "/software/reports-analytics" },
  { icon: Smartphone, name: "Self Service", href: "/software/employee-self-service" },
  { icon: Smartphone, name: "Mobile", href: "/software/mobile-workforce" },
  { icon: ShieldCheck, name: "Security", href: "/software/security-access" },
  { icon: FileText, name: "Documents", href: "/features" },
  { icon: Bell, name: "Notifications", href: "/features" },
  { icon: Workflow, name: "Approvals", href: "/features" },
  { icon: Building2, name: "Org Structure", href: "/features" },
];
